import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
// Still extends HTMLElement over Component so that refs are still available to parent components (e.g. SortingFilterComponent)
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();
    this.#openIfHashMatches();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
    window.addEventListener('hashchange', this.#openIfHashMatches, { signal });
    document.addEventListener('click', this.#handleAnchorClick, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }
  };

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const hash = window.location.hash;

    // If this accordion matches the URL hash, force it open
    if (this.details.id && hash === `#${this.details.id}`) {
      this.details.open = true;
      return;
    }

    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Opens the accordion if the current URL hash matches this accordion's details ID.
   */
  #openIfHashMatches = () => {
    const hash = window.location.hash;
    if (!hash) return;

    const detailsId = this.details.id;
    if (!detailsId) return;

    if (hash === `#${detailsId}`) {
      this.details.open = true;

      requestAnimationFrame(() => {
        this.details.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  };

  /**
   * Opens the accordion when a matching anchor link is clicked,
   * even if the hash is already the same.
   */
  #handleAnchorClick = (event) => {
    const target = event.target instanceof Element ? event.target.closest('a[href^="#"]') : null;
    if (!(target instanceof HTMLAnchorElement)) return;

    const hash = target.getAttribute('href');
    const detailsId = this.details.id;

    if (!hash || !detailsId) return;

    if (hash === `#${detailsId}`) {
      this.details.open = true;

      requestAnimationFrame(() => {
        this.details.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  };

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.details.open = false;
      this.summary.focus();
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}